import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Stack,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const modules = [
  { id: 'maintenance', name: 'Manutenzioni', price: 15 },
  { id: 'shop', name: 'Shop B2B', price: 10 },
  { id: 'sales', name: 'Vendite al banco', price: 10 },
  { id: 'rental', name: 'Noleggi', price: 5 },
  { id: 'accounting', name: 'Contabilità', price: 5 }
];

const PricingPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [numUsers, setNumUsers] = useState(1);
  const [numMechanics, setNumMechanics] = useState(1);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form dati utenti (array)
  const [usersData, setUsersData] = useState([{
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: ''
  }]);

  // Form dati azienda
  const [companyData, setCompanyData] = useState({
    companyName: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    phone: '',
    email: '',
    vatNumber: '',
    shippingName: '',
    shippingAddress: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingProvince: '',
    shippingPhone: ''
  });
  
  // Form dati meccanici (array) - solo se maintenance è selezionato
  const [mechanicsData, setMechanicsData] = useState([{
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  }]);
  
  // OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [otpResendError, setOtpResendError] = useState(null);
  
  // Mostra/nascondi password (array per ogni utente/meccanico)
  const [showUsersPassword, setShowUsersPassword] = useState([false]);
  const [showMechanicsPassword, setShowMechanicsPassword] = useState([false]);

  const steps = [
    'Numero Utenti',
    'Selezione Moduli',
    'Numero Meccanici',
    'Riepilogo e Prezzo',
    'Demo Gratuita'
  ];

  const calculatePrice = () => {
    let total = 0;

    // Primo utente + primo meccanico insieme: €15
    total += 15;

    // Utenti aggiuntivi: €5 ciascuno (dopo il primo)
    const additionalUsers = Math.max(0, numUsers - 1);
    total += additionalUsers * 5;

    // Meccanici aggiuntivi: €5 ciascuno (dopo il primo)
    const additionalMechanics = Math.max(0, numMechanics - 1);
    total += additionalMechanics * 5;

    // Primo modulo: €15
    if (selectedModules.length > 0) {
      total += 15;
    }

    // Moduli aggiuntivi (dal secondo in poi)
    if (selectedModules.length > 1) {
      selectedModules.slice(1).forEach(moduleId => {
        const module = modules.find(m => m.id === moduleId);
        if (module) {
          total += module.price;
        }
      });
    }

    // Sconto 15% se tutti i moduli sono selezionati
    if (selectedModules.length === modules.length) {
      total = total * 0.85;
    }

    return total;
  };

  const handleNext = () => {
    // Step 0: Numero Utenti
    if (activeStep === 0) {
      if (numUsers < 1) {
        alert('Inserisci almeno 1 utente');
        return;
      }
      // Aggiorna array utenti in base a numUsers
      setUsersData(prev => {
        const newUsers = [...prev];
        while (newUsers.length < numUsers) {
          newUsers.push({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            phoneNumber: ''
          });
        }
        while (newUsers.length > numUsers) {
          newUsers.pop();
        }
        return newUsers;
      });
      setShowUsersPassword(prev => {
        const newShow = [...prev];
        while (newShow.length < numUsers) {
          newShow.push(false);
        }
        while (newShow.length > numUsers) {
          newShow.pop();
        }
        return newShow;
      });
    }
    
    // Step 1: Selezione Moduli
    if (activeStep === 1) {
      if (selectedModules.length === 0) {
        alert('Seleziona almeno un modulo');
        return;
      }
      // Se maintenance è selezionato, imposta numMechanics a 1 se è 0
      if (selectedModules.includes('maintenance') && numMechanics === 0) {
        setNumMechanics(1);
        setMechanicsData([{
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        }]);
        setShowMechanicsPassword([false]);
      }
      // Se maintenance non è selezionato, salta lo step 2 (meccanici) e vai direttamente al riepilogo
      if (!selectedModules.includes('maintenance')) {
        setNumMechanics(0);
        setMechanicsData([{
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        }]);
        setShowMechanicsPassword([false]);
        setActiveStep(3); // Salta allo step 3 (Riepilogo)
        return;
      }
    }
    
    // Step 2: Numero Meccanici (solo se maintenance è selezionato)
    if (activeStep === 2) {
      const hasMaintenance = selectedModules.includes('maintenance');
      if (hasMaintenance) {
        if (numMechanics < 1) {
          alert('Inserisci almeno 1 meccanico');
          return;
        }
        // Aggiorna array meccanici in base a numMechanics
        setMechanicsData(prev => {
          const newMechanics = [...prev];
          while (newMechanics.length < numMechanics) {
            newMechanics.push({
              firstName: '',
              lastName: '',
              email: '',
              password: ''
            });
          }
          while (newMechanics.length > numMechanics) {
            newMechanics.pop();
          }
          return newMechanics;
        });
        setShowMechanicsPassword(prev => {
          const newShow = [...prev];
          while (newShow.length < numMechanics) {
            newShow.push(false);
          }
          while (newShow.length > numMechanics) {
            newShow.pop();
          }
          return newShow;
        });
      }
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    // Se siamo al riepilogo (step 3) e maintenance non è selezionato, torna allo step 1 (moduli)
    if (activeStep === 3 && !selectedModules.includes('maintenance')) {
      setActiveStep(1);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleModuleToggle = (moduleId) => {
    setSelectedModules((prev) => {
      const newModules = prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId];
      
      // Se si seleziona "maintenance", imposta numMechanics a 1 se è 0
      if (moduleId === 'maintenance' && newModules.includes('maintenance') && numMechanics === 0) {
        setNumMechanics(1);
        setMechanicsData([{
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        }]);
        setShowMechanicsPassword([false]);
      }
      
      // Se si deseleziona "maintenance", resetta i meccanici
      if (moduleId === 'maintenance' && !newModules.includes('maintenance')) {
        setNumMechanics(0);
        setMechanicsData([{
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        }]);
        setShowMechanicsPassword([false]);
      }
      
      return newModules;
    });
  };

  const handleUserDataChange = (index, field, value) => {
    setUsersData(prev => {
      const newUsers = [...prev];
      newUsers[index] = { ...newUsers[index], [field]: value };
      return newUsers;
    });
    setError(null);
  };

  const handleCompanyDataChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleMechanicDataChange = (index, field, value) => {
    setMechanicsData(prev => {
      const newMechanics = [...prev];
      newMechanics[index] = { ...newMechanics[index], [field]: value };
      return newMechanics;
    });
    setError(null);
  };

  const validateForm = () => {
    // Valida dati azienda
    if (!companyData.companyName || !companyData.vatNumber) {
      setError('Compila tutti i campi obbligatori dell\'azienda (Nome Azienda e Partita IVA)');
      return false;
    }

    // Valida tutti gli utenti
    for (let i = 0; i < usersData.length; i++) {
      const user = usersData[i];
      if (!user.firstName || !user.lastName || !user.email || !user.password) {
        setError(`Compila tutti i campi dell'utente ${i + 1}`);
        return false;
      }
      if (user.password.length < 6) {
        setError(`La password dell'utente ${i + 1} deve essere di almeno 6 caratteri`);
        return false;
      }
    }

    // Valida meccanici solo se maintenance è selezionato
    const hasMaintenance = selectedModules.includes('maintenance');
    if (hasMaintenance && numMechanics > 0) {
      for (let i = 0; i < mechanicsData.length; i++) {
        const mechanic = mechanicsData[i];
        if (!mechanic.firstName || !mechanic.lastName || !mechanic.email || !mechanic.password) {
          setError(`Compila tutti i campi del meccanico ${i + 1}`);
          return false;
        }
        if (mechanic.password.length < 6) {
          setError(`La password del meccanico ${i + 1} deve essere di almeno 6 caratteri`);
          return false;
        }
      }
    }

    return true;
  };

  // Timer per il riinvio OTP
  useEffect(() => {
    let interval = null;
    if (otpResendTimer > 0) {
      interval = setInterval(() => {
        setOtpResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (otpResendTimer === 0 && interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpResendTimer]);

  // Funzione per verificare se l'utente esiste già
  const checkUserExists = async (email, password) => {
    try {
      // Prova a fare login con la password corretta
      // Se il login funziona, l'utente esiste ed è stato creato
      await api.post('/auth/login', {
        email: email,
        password: password
      });
      return true; // Il login funziona, l'utente esiste
    } catch (err) {
      // Se l'errore è "Credenziali non valide", potrebbe significare che l'utente esiste ma la password è sbagliata
      // Ma in questo caso, se abbiamo appena creato l'utente, la password dovrebbe essere corretta
      const errorMsg = err.response?.data?.msg || '';
      // Se l'errore è "Utente non trovato", l'utente non esiste
      if (errorMsg.includes('non trovato') || errorMsg.includes('not found')) {
        return false;
      }
      // Altrimenti, l'utente potrebbe esistere (ma con password diversa o altro problema)
      // Per sicurezza, assumiamo che se non è "non trovato", l'utente esiste
      return true;
    }
  };

  const handleCreateDemo = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setOtpResendError(null);

    try {
      // Prepara i dati: il primo utente è l'admin, gli altri sono utenti normali
      const adminUser = usersData[0];
      const additionalUsers = usersData.slice(1);

      // Prepara i meccanici (solo se maintenance è selezionato)
      const hasMaintenance = selectedModules.includes('maintenance');
      const mechanicsToSend = hasMaintenance ? mechanicsData : [];

      const response = await api.post('/auth/create-demo', {
        user: {
          firstName: adminUser.firstName,
          lastName: adminUser.lastName,
          email: adminUser.email,
          password: adminUser.password,
          companyName: companyData.companyName,
          phoneNumber: adminUser.phoneNumber,
          address: companyData.address,
          city: companyData.city,
          postalCode: companyData.postalCode,
          province: companyData.province,
          phone: companyData.phone,
          vatNumber: companyData.vatNumber,
          shippingName: companyData.shippingName,
          shippingAddress: companyData.shippingAddress,
          shippingCity: companyData.shippingCity,
          shippingPostalCode: companyData.shippingPostalCode,
          shippingProvince: companyData.shippingProvince,
          shippingPhone: companyData.shippingPhone
        },
        additionalUsers: additionalUsers.map(u => ({
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          password: u.password,
          phoneNumber: u.phoneNumber
        })),
        mechanic: mechanicsToSend.length > 0 ? {
          firstName: mechanicsToSend[0].firstName,
          lastName: mechanicsToSend[0].lastName,
          email: mechanicsToSend[0].email,
          password: mechanicsToSend[0].password
        } : null,
        additionalMechanics: mechanicsToSend.slice(1).map(m => ({
          firstName: m.firstName,
          lastName: m.lastName,
          email: m.email,
          password: m.password
        })),
        company: companyData,
        modules: selectedModules,
        numUsers,
        numMechanics: hasMaintenance ? numMechanics : 0
      });

      if (response.data.success) {
        setOtpSent(true);
      }
    } catch (err) {
      console.error('Errore nella creazione demo:', err);
      const errorMessage = err.response?.data?.message || 'Errore nella creazione della demo. Riprova.';
      setError(errorMessage);
      
      // Avvia il timer di 60 secondi per il riinvio
      setOtpResendTimer(60);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (otpResendTimer > 0) {
      return; // Non permettere il riinvio se il timer è ancora attivo
    }

    setLoading(true);
    setOtpResendError(null);

    try {
      // Prima verifica se l'utente è stato creato
      const adminUser = usersData[0];
      const userExists = await checkUserExists(adminUser.email, adminUser.password);

      if (userExists) {
        // L'utente è stato creato, mostra messaggio di successo e reindirizza al login
        setError(null);
        setOtpResendError(null);
        alert('Il tuo account è stato creato con successo! Verrai reindirizzato alla pagina di login.');
        navigate('/login');
        return;
      }

      // Se l'utente non esiste, riprova a creare la demo
      await handleCreateDemo();
    } catch (err) {
      console.error('Errore nel riinvio OTP:', err);
      setOtpResendError('Errore nel riinvio del codice OTP. Riprova.');
      // Avvia di nuovo il timer
      setOtpResendTimer(60);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Inserisci un codice OTP valido (6 cifre)');
      return;
    }

    setLoading(true);
    setOtpError(null);

    try {
      const response = await api.post('/auth/verify-demo-otp', {
        email: usersData[0].email,
        otp: otp
      });

      if (response.data.success) {
        // Reindirizza al login
        navigate('/login', {
          state: {
            message: 'Demo creata con successo! Accedi con le tue credenziali.',
            demoEmail: usersData[0]?.email || ''
          }
        });
      }
    } catch (err) {
      console.error('Errore nella verifica OTP:', err);
      setOtpError(err.response?.data?.message || 'Codice OTP non valido. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
              Quanti utenti avrà bisogno la tua azienda?
            </Typography>
            <TextField
              type="number"
              label="Numero Utenti"
              value={numUsers}
              onChange={(e) => setNumUsers(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              fullWidth
              sx={{ maxWidth: 300, mx: 'auto' }}
            />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              Il primo utente è incluso nel pacchetto base
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
              Quali moduli vuoi attivare?
            </Typography>
            <Grid container spacing={2} sx={{ maxWidth: 800, mx: 'auto' }}>
              {modules.map((module) => (
                <Grid item xs={12} sm={6} key={module.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedModules.includes(module.id) ? 2 : 1,
                      borderColor: selectedModules.includes(module.id) ? 'primary.main' : 'divider',
                      bgcolor: selectedModules.includes(module.id) ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      }
                    }}
                    onClick={() => handleModuleToggle(module.id)}
                  >
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedModules.includes(module.id)}
                            onChange={() => handleModuleToggle(module.id)}
                          />
                        }
                        label={module.name}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {selectedModules.length === modules.length && (
              <Alert severity="success" sx={{ mt: 3, maxWidth: 800, mx: 'auto' }}>
                🎉 Sconto del 15% applicato! Hai selezionato tutti i moduli.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
              Quanti meccanici lavorano nella tua officina?
            </Typography>
            <TextField
              type="number"
              label="Numero Meccanici"
              value={numMechanics}
              onChange={(e) => setNumMechanics(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              fullWidth
              sx={{ maxWidth: 300, mx: 'auto' }}
            />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              Il primo meccanico è incluso nel pacchetto base
            </Typography>
          </Box>
        );

      case 3:
        const basePrice = 15; // Primo utente + primo meccanico insieme
        const additionalUsersCost = Math.max(0, numUsers - 1) * 5;
        const additionalMechanicsCost = Math.max(0, numMechanics - 1) * 5;
        const firstModuleCost = selectedModules.length > 0 ? 15 : 0;
        const additionalModulesCost = selectedModules.length > 1
          ? selectedModules.slice(1).reduce((sum, moduleId) => {
              const module = modules.find(m => m.id === moduleId);
              return sum + (module ? module.price : 0);
            }, 0)
          : 0;
        const subtotal = basePrice + additionalUsersCost + additionalMechanicsCost + firstModuleCost + additionalModulesCost;
        const discount = selectedModules.length === modules.length ? subtotal * 0.15 : 0;
        const price = subtotal - discount;

        return (
          <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
              Riepilogo e Prezzo
            </Typography>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Configurazione
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  <strong>Utenti:</strong> {numUsers}
                </Typography>
                <Typography variant="body1">
                  <strong>Meccanici:</strong> {numMechanics}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>Moduli selezionati:</strong>
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  {selectedModules.map((moduleId) => {
                    const module = modules.find(m => m.id === moduleId);
                    return (
                      <li key={moduleId}>
                        {module?.name}
                      </li>
                    );
                  })}
                </Box>
                {selectedModules.length === modules.length && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                    🎉 Sconto del 15% applicato! Hai selezionato tutti i moduli.
                  </Typography>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">
                  <strong>Totale mensile:</strong>
                </Typography>
                <Typography variant="h4" color="primary">
                  €{price.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Demo Gratuita di 3 Giorni
              </Typography>
              <Typography variant="body2">
                Attiva una demo gratuita di 3 giorni senza vincoli e senza carta di credito. 
                Prova tutte le funzionalità selezionate senza impegno.
              </Typography>
            </Alert>
          </Box>
        );

      case 4:
        if (otpSent) {
          return (
            <Box sx={{ mt: 4, maxWidth: 500, mx: 'auto' }}>
              <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
                Verifica Email
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Ti abbiamo inviato un codice OTP all'indirizzo <strong>{usersData[0]?.email || ''}</strong>. 
                Inserisci il codice ricevuto per completare l'attivazione della demo.
              </Alert>
              <TextField
                label="Codice OTP"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setOtpError(null);
                }}
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                fullWidth
                error={!!otpError}
                helperText={otpError}
                sx={{ mb: 3 }}
                placeholder="000000"
              />
              {otpResendError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {otpResendError}
                </Alert>
              )}
              {otpResendTimer > 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  Puoi richiedere un nuovo codice tra {otpResendTimer} secondi
                </Typography>
              )}
              {otpResendTimer === 0 && error && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    Richiedi nuovo codice OTP
                  </Button>
                </Box>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
                fullWidth
                sx={{ mb: 2 }}
              >
                {loading ? 'Verifica in corso...' : 'Verifica Codice'}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                  setOtpError(null);
                  setOtpResendTimer(0);
                  setOtpResendError(null);
                }}
                fullWidth
              >
                Torna indietro
              </Button>
            </Box>
          );
        }

        return (
          <Box sx={{ mt: 4, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
              Crea la Tua Demo Gratuita
            </Typography>
            <Alert severity="info" sx={{ mb: 4 }}>
              Compila i dati per creare la tua demo gratuita di 3 giorni. 
              Riceverai un codice OTP via email per completare l'attivazione.
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Dati Utenti */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Dati Utenti ({numUsers} {numUsers === 1 ? 'utente' : 'utenti'})
              </Typography>
              <Grid container spacing={3}>
                {usersData.map((user, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                        {index === 0 ? 'Utente Admin' : `Utente ${index + 1}`}
                      </Typography>
                      <Stack spacing={2}>
                        <TextField
                          label="Nome"
                          value={user.firstName}
                          onChange={(e) => handleUserDataChange(index, 'firstName', e.target.value)}
                          fullWidth
                          required
                        />
                        <TextField
                          label="Cognome"
                          value={user.lastName}
                          onChange={(e) => handleUserDataChange(index, 'lastName', e.target.value)}
                          fullWidth
                          required
                        />
                        <TextField
                          label="Email"
                          type="email"
                          value={user.email}
                          onChange={(e) => handleUserDataChange(index, 'email', e.target.value)}
                          fullWidth
                          required
                        />
                        {index === 0 && (
                          <TextField
                            label="Numero di Telefono"
                            type="tel"
                            value={user.phoneNumber}
                            onChange={(e) => handleUserDataChange(index, 'phoneNumber', e.target.value)}
                            fullWidth
                            helperText="Es: 3331234567 o +393331234567"
                          />
                        )}
                        <TextField
                          label="Password"
                          type={showUsersPassword[index] ? 'text' : 'password'}
                          value={user.password}
                          onChange={(e) => handleUserDataChange(index, 'password', e.target.value)}
                          fullWidth
                          required
                          helperText="Minimo 6 caratteri"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => {
                                    const newShow = [...showUsersPassword];
                                    newShow[index] = !newShow[index];
                                    setShowUsersPassword(newShow);
                                  }}
                                  edge="end"
                                >
                                  {showUsersPassword[index] ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                        {index === 0 && (
                          <TextField
                            label="Nome Azienda"
                            value={companyData.companyName}
                            onChange={(e) => {
                              handleCompanyDataChange('companyName', e.target.value);
                            }}
                            fullWidth
                            required
                          />
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Dati Meccanici - Solo se maintenance è selezionato */}
            {selectedModules.includes('maintenance') && numMechanics > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Dati Meccanici ({numMechanics} {numMechanics === 1 ? 'meccanico' : 'meccanici'})
                </Typography>
                <Grid container spacing={3}>
                  {mechanicsData.map((mechanic, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                          Meccanico {index + 1}
                        </Typography>
                        <Stack spacing={2}>
                          <TextField
                            label="Nome"
                            value={mechanic.firstName}
                            onChange={(e) => handleMechanicDataChange(index, 'firstName', e.target.value)}
                            fullWidth
                            required
                          />
                          <TextField
                            label="Cognome"
                            value={mechanic.lastName}
                            onChange={(e) => handleMechanicDataChange(index, 'lastName', e.target.value)}
                            fullWidth
                            required
                          />
                          <TextField
                            label="Email"
                            type="email"
                            value={mechanic.email}
                            onChange={(e) => handleMechanicDataChange(index, 'email', e.target.value)}
                            fullWidth
                            required
                          />
                          <TextField
                            label="Password"
                            type={showMechanicsPassword[index] ? 'text' : 'password'}
                            value={mechanic.password}
                            onChange={(e) => handleMechanicDataChange(index, 'password', e.target.value)}
                            fullWidth
                            required
                            helperText="Minimo 6 caratteri"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => {
                                      const newShow = [...showMechanicsPassword];
                                      newShow[index] = !newShow[index];
                                      setShowMechanicsPassword(newShow);
                                    }}
                                    edge="end"
                                  >
                                    {showMechanicsPassword[index] ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Dati Azienda */}
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Informazioni Azienda e Fatturazione
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Indirizzo"
                      value={companyData.address}
                      onChange={(e) => handleCompanyDataChange('address', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Città"
                      value={companyData.city}
                      onChange={(e) => handleCompanyDataChange('city', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="CAP"
                      value={companyData.postalCode}
                      onChange={(e) => handleCompanyDataChange('postalCode', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Provincia"
                      value={companyData.province}
                      onChange={(e) => handleCompanyDataChange('province', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Telefono"
                      value={companyData.phone}
                      onChange={(e) => handleCompanyDataChange('phone', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Email Azienda"
                      type="email"
                      value={companyData.email}
                      onChange={(e) => handleCompanyDataChange('email', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Partita IVA"
                      value={companyData.vatNumber}
                      onChange={(e) => handleCompanyDataChange('vatNumber', e.target.value)}
                      fullWidth
                      required
                      helperText="La partita IVA deve essere univoca"
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Indirizzo di Spedizione
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Nome Destinatario"
                      value={companyData.shippingName}
                      onChange={(e) => handleCompanyDataChange('shippingName', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Indirizzo di Spedizione"
                      value={companyData.shippingAddress}
                      onChange={(e) => handleCompanyDataChange('shippingAddress', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Città di Spedizione"
                      value={companyData.shippingCity}
                      onChange={(e) => handleCompanyDataChange('shippingCity', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="CAP di Spedizione"
                      value={companyData.shippingPostalCode}
                      onChange={(e) => handleCompanyDataChange('shippingPostalCode', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Provincia di Spedizione"
                      value={companyData.shippingProvince}
                      onChange={(e) => handleCompanyDataChange('shippingProvince', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Telefono di Spedizione"
                      value={companyData.shippingPhone}
                      onChange={(e) => handleCompanyDataChange('shippingPhone', e.target.value)}
                      fullWidth
                    />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCreateDemo}
                disabled={loading}
                sx={{ px: 6, py: 1.5 }}
              >
                {loading ? 'Creazione in corso...' : 'Crea Demo e Invia OTP'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 4 }}
        >
          Torna alla Home
        </Button>

        <Typography variant="h3" component="h1" align="center" gutterBottom sx={{ mb: 2 }}>
          Scegli il Tuo Piano
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Configura il piano perfetto per la tua officina
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper sx={{ p: 4, minHeight: 400 }}>
          {renderStepContent()}
        </Paper>

        {!otpSent && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Indietro
            </Button>
            {activeStep < steps.length - 1 && (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                {activeStep === steps.length - 2 ? 'Conferma' : 'Avanti'}
              </Button>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PricingPage;


